import React, {
  ChangeEvent,
  FC,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import sortBy from "lodash/sortBy";

import Button from "../../../../components/Button";
import ButtonPrimary from "../../../../components/ButtonPrimary";
import Modal from "../../../../components/Modal";
import { ModalHeader } from "../../../../components/Modal/ModalHeader";
import { ModalContent } from "../../../../components/Modal/ModalContent";
import { ModalFooter } from "../../../../components/Modal/ModalFooter";
import FormGroup from "../../../../components/FormGroup";
import Label from "../../../../components/Label";
import Input from "../../../../components/Input";
import Select from "../../../../components/Select";
import Textarea from "../../../../components/Textarea";
import { ISKU } from "../../../../store/skuMapping";
import { PROVIDERS } from "../../../../utils/common/contants";
import { IOfferingModalProps } from "./types";
import DynamicList from "../DynamicList";
import { IProductLine } from "../../../../services/api/productLine/types";
import {
  IOfferingSKURank,
  Offering,
  SKUWrapper,
} from "../../../../services/api/offering/types";
import { getSKUQuery } from "./utils";
import reducer, {
  addSKU,
  editSKU,
  initialState,
  removeSKU,
  setCode,
  setDescription,
  setEndDate,
  setName,
  setProductLineCode,
  setSKU,
  setSpCode,
} from "./store";
import ReactSelect from "../../../../components/ReactSelect";

import "./styles.scss";

export const OfferingModal: FC<IOfferingModalProps> = ({
  onDismiss,
  onConfirm,
  title,
  confirmText,
  offering,
  tenantCode,
  productLines,
  onSKUUpdate,
  SKUList,
  isLoading,
  onClientError,
  codeOfferingError,
  ...props
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const isFieldsDisabled = Boolean(offering);
  const [isCodeDirty, setIsCodeDirty] = useState(false);

  const filteredProductLines = useMemo(() => {
    if (tenantCode === "all") {
      return productLines;
    }

    return productLines.filter((item: IProductLine) => {
      return item.tenant_code === tenantCode;
    });
  }, [productLines, tenantCode]);

  useEffect(() => {
    if (offering) {
      setOfferData(offering);
    }
  }, [offering]);

  useEffect(() => {
    const { spCode, productLineCode } = state;
    if (spCode && productLineCode) {
      const params = {
        q: getSKUQuery(spCode, productLineCode),
      };

      onSKUUpdate(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.spCode, state.productLineCode]);

  useEffect(() => {
    onClientError(state.code);
  }, [state.code, onClientError]);

  const onUpdateCode = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCode(event.target.value));
  };

  const codeBlurHandler = () => {
    setIsCodeDirty(true);
  };

  /* eslint-disable */
  const setOfferData = ({
    name,
    code,
    description,
    ends_at,
    product_line_code,
    sp_code,
    skus,
  }: Offering) => {
    const SKUList = skus.map(
      (offeringSKU: SKUWrapper): IOfferingSKURank => {
        return {
          rank: offeringSKU.rank,
          id: offeringSKU.sku.id,
        };
      },
    );

    dispatch(setName(name));
    dispatch(setCode(code));
    dispatch(setDescription(description));

    try {
      dispatch(setEndDate(ends_at.substring(0, 10)));
    } catch {}

    dispatch(setProductLineCode(product_line_code));
    dispatch(setSpCode(sp_code));
    dispatch(setSKU(sortBy(SKUList, "rank")));
  };

  const handleSubmit = () => {
    const {
      name,
      code,
      productLineCode,
      spCode,
      description,
      endDate,
      skus,
    } = state;
    const skuIds = skus
      .map((item) => {
        return item.id;
      })
      .filter((id) => {
        return !!id;
      }) as string[];

    // BE require props in snake-case
    /* eslint-disable */
    onConfirm({
      name,
      code,
      description,
      sku_ids: skuIds,
      sp_code: spCode,
      product_line_code: productLineCode,
      ends_at: endDate,
    });
  };

  const onUpdateName = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setName(event.target.value));
  };

  const onUpdateProductLine = (event: ChangeEvent<HTMLSelectElement>) => {
    dispatch(setProductLineCode(event.target.value));
  };

  const onUpdateSubscriptionProvider = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    dispatch(setSpCode(event.target.value));
  };

  const onUpdateEndDate = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setEndDate(event.target.value));
  };

  const onUpdateDescription = (event: ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(setDescription(event.target.value));
  };

  const onAddSKUs = () => {
    dispatch(addSKU());
  };

  const removeSKUListItem = () => {
    dispatch(removeSKU());
  };

  const handleSKUpdate = (option: ISKU, rank: number) => {
    dispatch(editSKU({ rank, id: option.id }));
  };

  const getSkuOptions = (rank: number) => {
    if (state.productLineCode && state.spCode) {
      return SKUList.filter((sku: ISKU) => {
        return !state.skus.some((selectedSKU: IOfferingSKURank) => {
          return selectedSKU.id === sku.id && selectedSKU.rank !== rank;
        });
      });
    }
    return [];
  };

  const SKUOptions = useMemo(() => {
    return state.skus.map((sku: IOfferingSKURank) => {
      return getSkuOptions(sku.rank);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.skus, SKUList]);

  const getValueForSelect = (options: ISKU[], id: string) => {
    return options.find((option: ISKU) => option.id === id);
  };

  const filterSKUOptions = (
    option: { label: string; value: string; data: ISKU },
    input: string,
  ) => {
    return option.label.toLowerCase().includes(input.trim().toLowerCase());
  };

  const renderListItem = (sku: IOfferingSKURank) => {
    return (
      <FormGroup>
        <Label>{`${t("SKU")} ${sku.rank + 1}`}</Label>
        <ReactSelect
          name={`sku-select-${sku.rank + 1}`}
          id={`sku-select-${sku.rank + 1}`}
          getOptionLabel={(option: ISKU) => option.sp_sku_code}
          getOptionValue={(option: ISKU) => option.id}
          options={SKUOptions[sku.rank]}
          onChange={(option: ISKU) => handleSKUpdate(option, sku.rank)}
          value={getValueForSelect(SKUOptions[sku.rank], sku.id || "")}
          filterOption={filterSKUOptions}
          isSearchable
          placeholder={t("chooseSKU")}
        />
      </FormGroup>
    );
  };

  const SKUListItems = useMemo(() => {
    return state.skus.map((SKU: IOfferingSKURank) => {
      return renderListItem(SKU);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.skus, SKUOptions]);

  const isSKUsValid = () => {
    const validSKU = state.skus.filter(({ id }: IOfferingSKURank) => {
      return Boolean(id);
    });
    return validSKU.length > 0 && validSKU.length === state.skus.length;
  };

  const isValid = useMemo(() => {
    return (
      Boolean(state.name) &&
      Boolean(state.code) &&
      Boolean(state.description) &&
      Boolean(state.spCode) &&
      Boolean(state.productLineCode) &&
      !codeOfferingError.error &&
      isSKUsValid()
    );
  }, [
    state.name,
    state.code,
    state.description,
    state.spCode,
    state.productLineCode,
    state.skus,
    codeOfferingError.error,
  ]);

  return (
    <Modal className="ams-offering-modal" {...props}>
      <ModalHeader className="ams-offering-modal__header">{title}</ModalHeader>

      <ModalContent className="ams-offering-modal__content">
        <FormGroup>
          <Label>{t("offerName")}</Label>
          <Input
            required
            id="offering-name-input"
            value={state.name}
            onChange={onUpdateName}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("offerCode")}</Label>
          <Input
            required
            id="offering-code-input"
            value={state.code}
            disabled={isFieldsDisabled}
            error={isCodeDirty && codeOfferingError.error}
            errorText={codeOfferingError.errorMessage}
            onChange={onUpdateCode}
            onBlur={codeBlurHandler}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("productLine")}</Label>
          <Select
            required
            id="product-line-select"
            value={state.productLineCode}
            disabled={isFieldsDisabled}
            onChange={onUpdateProductLine}
          >
            <option value="">{t("chooseProductLine")}</option>
            {filteredProductLines.map((item: IProductLine) => {
              return (
                <option key={item.code} value={item.code}>
                  {item.code}
                </option>
              );
            })}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>{t("subscriptionProvider")}</Label>
          <Select
            required
            id="subscription-provider-select"
            value={state.spCode}
            disabled={isFieldsDisabled}
            onChange={onUpdateSubscriptionProvider}
          >
            <option value="">{t("chooseSubscriptionProvider")}</option>
            {PROVIDERS.map((provider: string) => {
              return (
                <option key={provider} value={provider}>
                  {t(provider)}
                </option>
              );
            })}
          </Select>
        </FormGroup>

        <FormGroup className="ams-offering-modal__date">
          <Label>{t("endDate")}</Label>
          <Input
            type="date"
            id="end-date-input"
            value={state.endDate}
            onChange={onUpdateEndDate}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("description")}</Label>
          <Textarea
            required
            id="description-textarea"
            value={state.description}
            onChange={onUpdateDescription}
          />
        </FormGroup>

        <DynamicList
          items={SKUListItems}
          onAdd={onAddSKUs}
          onRemove={removeSKUListItem}
        />
      </ModalContent>

      <ModalFooter>
        <Button id="cancel-button" onClick={onDismiss}>
          {t("cancel")}
        </Button>
        <ButtonPrimary
          className="ams-modal__action"
          id="confirm-button"
          isLoading={isLoading}
          disabled={!isValid}
          onClick={handleSubmit}
        >
          {confirmText}
        </ButtonPrimary>
      </ModalFooter>
    </Modal>
  );
};

OfferingModal.defaultProps = {
  title: i18next.t("newOffering"),
  confirmText: i18next.t("addOffering"),
};
